import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Search,
  DollarSign,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Mail,
  Plus,
  RefreshCw,
  Loader2,
  Calendar,
  Phone,
  ExternalLink,
  Send,
  CreditCard,
  Receipt,
  AlertTriangle,
  History,
  ChevronRight,
  MessageSquare,
  PhoneCall,
  User,
  FileWarning,
  Lightbulb,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { API_URL, API_BASE_URL } from "../../config/api";

/* global Office */

export default function FinanceAddinPage() {
  // Office.js loading state
  const [officeLoaded, setOfficeLoaded] = useState(false);
  const [isOutlook, setIsOutlook] = useState(false);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem("outlook_finance_token"));
  const [currentUser, setCurrentUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Email context
  const [currentEmail, setCurrentEmail] = useState(null);
  const [emailBody, setEmailBody] = useState("");

  // Active tab
  const [activeTab, setActiveTab] = useState("vendor");

  // Vendor data
  const [vendorData, setVendorData] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  // Payment Orders
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [paymentOrdersLoading, setPaymentOrdersLoading] = useState(false);
  const [showCreatePaymentOrder, setShowCreatePaymentOrder] = useState(false);
  const [newPaymentOrder, setNewPaymentOrder] = useState({
    vendor_id: "",
    vendor_name: "",
    amount: "",
    currency: "EUR",
    invoice_number: "",
    invoice_date: "",
    due_date: "",
    description: "",
    priority: "normal"
  });
  
  // Pantheon customer search
  const [pantheonSearchQuery, setPantheonSearchQuery] = useState("");
  const [pantheonSearchResults, setPantheonSearchResults] = useState([]);
  const [pantheonSearchLoading, setPantheonSearchLoading] = useState(false);
  const [showPantheonDropdown, setShowPantheonDropdown] = useState(false);
  const [requestingNewVendor, setRequestingNewVendor] = useState(false);

  // Solicitation History
  const [solicitationHistory, setSolicitationHistory] = useState([]);
  const [solicitationLoading, setSolicitationLoading] = useState(false);
  const [showLogSolicitation, setShowLogSolicitation] = useState(false);
  const [newSolicitation, setNewSolicitation] = useState({
    channel: "email",
    direction: "inbound",
    topic: "payment_request",
    urgency: "medium",
    notes: "",
    follow_up_date: "",
    vendor_id: "",
    vendor_name: "",
  });
  
  // Solicitation vendor search
  const [solicitationVendorQuery, setSolicitationVendorQuery] = useState("");
  const [solicitationVendorResults, setSolicitationVendorResults] = useState([]);
  const [solicitationVendorLoading, setSolicitationVendorLoading] = useState(false);
  const [showSolicitationVendorDropdown, setShowSolicitationVendorDropdown] = useState(false);

  // Suggestion Box
  const [suggestionText, setSuggestionText] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // Pending Invoices
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${authToken}` }
  }), [authToken]);

  // Submit suggestion
  const submitSuggestion = async () => {
    if (!suggestionText.trim()) {
      toast.error("Please enter your suggestion");
      return;
    }

    setSubmittingSuggestion(true);
    try {
      await axios.post(`${API_URL}/outlook/suggestions`, {
        suggestion: suggestionText,
        plugin: "finance",
        user_email: currentUser?.email,
        user_name: currentUser?.full_name,
        email_context: currentEmail?.subject,
      }, getAuthHeaders());

      toast.success("Thank you for your suggestion!");
      setSuggestionText("");
    } catch (error) {
      toast.error("Failed to submit suggestion");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  // Load Office.js dynamically
  useEffect(() => {
    if (typeof window.Office !== "undefined") {
      setOfficeLoaded(true);
      setIsOutlook(!!window.Office.context?.mailbox);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://appsforoffice.microsoft.com/lib/1.1/hosted/office.js";
    script.async = true;
    script.onload = () => {
      setOfficeLoaded(true);
      if (typeof window.Office !== "undefined") {
        window.Office.onReady((info) => {
          setIsOutlook(info.host === window.Office.HostType.Outlook);
        });
      }
    };
    script.onerror = () => {
      setOfficeLoaded(true);
      setIsOutlook(false);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize email context when Office is ready
  useEffect(() => {
    if (!officeLoaded || !isOutlook) return;

    try {
      window.Office.onReady(() => {
        try {
          const item = window.Office.context.mailbox.item;
          if (item) {
            const emailData = {
              message_id: item.itemId || `temp_${Date.now()}`,
              conversation_id: item.conversationId,
              subject: item.subject || "",
              sender_email: item.from?.emailAddress || "",
              sender_name: item.from?.displayName || "",
              recipients: item.to?.map((r) => r.emailAddress) || [],
              received_at: item.dateTimeCreated?.toISOString(),
            };
            setCurrentEmail(emailData);

            item.body.getAsync(window.Office.CoercionType.Text, (result) => {
              if (result.status === window.Office.AsyncResultStatus.Succeeded) {
                setEmailBody(result.value.substring(0, 2000));
              }
            });
          }
        } catch (e) {
          console.error("Error reading email:", e);
        }
      });
    } catch (e) {
      console.error("Office.onReady error:", e);
    }
  }, [officeLoaded, isOutlook]);

  // Check auth on load
  useEffect(() => {
    if (authToken) {
      verifyAuth();
    }
  }, [authToken]);

  // Auto-lookup vendor when email changes
  useEffect(() => {
    if (isAuthenticated && currentEmail?.sender_email) {
      lookupVendor(currentEmail.sender_email);
    }
  }, [isAuthenticated, currentEmail?.sender_email]);

  const verifyAuth = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`, getAuthHeaders());
      setCurrentUser(res.data);
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
      localStorage.removeItem("outlook_finance_token");
      setAuthToken(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const token = response.data.access_token;
      localStorage.setItem("outlook_finance_token", token);
      setAuthToken(token);
      setIsAuthenticated(true);
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("outlook_finance_token");
    setAuthToken(null);
    setIsAuthenticated(false);
    setVendorData(null);
    toast.success("Logged out");
  };

  // Lookup vendor by email domain
  const lookupVendor = async (emailAddress) => {
    if (!emailAddress) return;
    
    const domain = emailAddress.split("@")[1];
    if (!domain) return;

    setVendorLoading(true);
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/vendors`, {
        params: { search: domain },
        ...getAuthHeaders()
      });

      if (response.data.vendors?.length > 0) {
        const vendor = response.data.vendors[0];
        setVendorData({ vendor, found: true });
        
        // Load vendor's payment orders and solicitation history
        loadPaymentOrders(vendor.id);
        loadSolicitationHistory(vendor.id);
        loadPendingInvoices(vendor.id);
        
        // Pre-fill new payment order
        setNewPaymentOrder(prev => ({
          ...prev,
          vendor_id: vendor.id,
          vendor_name: vendor.name
        }));
      } else {
        setVendorData({ found: false, searchedEmail: emailAddress, domain });
      }
    } catch (error) {
      console.error("Vendor lookup failed:", error);
      setVendorData({ found: false, searchedEmail: emailAddress });
    } finally {
      setVendorLoading(false);
    }
  };

  // Load payment orders for vendor
  const loadPaymentOrders = async (vendorId) => {
    setPaymentOrdersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/payment-orders`, {
        params: { vendor_id: vendorId, status: "pending" },
        ...getAuthHeaders()
      });
      setPaymentOrders(response.data.payment_orders || []);
    } catch (error) {
      console.error("Failed to load payment orders:", error);
      setPaymentOrders([]);
    } finally {
      setPaymentOrdersLoading(false);
    }
  };

  // Load solicitation history
  const loadSolicitationHistory = async (vendorId) => {
    setSolicitationLoading(true);
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/contact-events`, {
        params: { vendor_id: vendorId, limit: 20 },
        ...getAuthHeaders()
      });
      setSolicitationHistory(response.data.contact_events || []);
    } catch (error) {
      console.error("Failed to load solicitation history:", error);
      setSolicitationHistory([]);
    } finally {
      setSolicitationLoading(false);
    }
  };

  // Load pending invoices
  const loadPendingInvoices = async (vendorId) => {
    setInvoicesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/vendor-ap/invoices`, {
        params: { vendor_id: vendorId, status: "pending" },
        ...getAuthHeaders()
      });
      setPendingInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Failed to load invoices:", error);
      setPendingInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Create payment order
  const createPaymentOrder = async () => {
    if (!newPaymentOrder.amount || !newPaymentOrder.vendor_name) {
      toast.error("Amount and vendor are required");
      return;
    }

    try {
      await axios.post(`${API_URL}/vendor-ap/payment-orders`, {
        ...newPaymentOrder,
        source: "outlook_finance_addin",
        email_subject: currentEmail?.subject,
        email_from: currentEmail?.sender_email,
      }, getAuthHeaders());

      toast.success("Payment order created!");
      setShowCreatePaymentOrder(false);
      setNewPaymentOrder({
        vendor_id: vendorData?.vendor?.id || "",
        vendor_name: vendorData?.vendor?.name || "",
        amount: "",
        currency: "EUR",
        invoice_number: "",
        invoice_date: "",
        due_date: "",
        description: "",
        priority: "normal"
      });
      setPantheonSearchQuery("");
      setPantheonSearchResults([]);
      
      if (vendorData?.vendor?.id) {
        loadPaymentOrders(vendorData.vendor.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment order");
    }
  };

  // Search Pantheon customers
  const searchPantheonCustomers = async (query) => {
    if (!query || query.length < 2) {
      setPantheonSearchResults([]);
      return;
    }
    
    setPantheonSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/search-companies`, {
        params: { query: query, limit: 10 },
        ...getAuthHeaders()
      });
      setPantheonSearchResults(response.data.companies || []);
      setShowPantheonDropdown(true);
    } catch (error) {
      console.error("Failed to search Pantheon customers:", error);
      setPantheonSearchResults([]);
    } finally {
      setPantheonSearchLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (pantheonSearchQuery) {
        searchPantheonCustomers(pantheonSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [pantheonSearchQuery]);

  // Select Pantheon customer
  const selectPantheonCustomer = (company) => {
    setNewPaymentOrder(prev => ({
      ...prev,
      vendor_id: company.id,
      vendor_name: company.name
    }));
    setPantheonSearchQuery(company.name);
    setShowPantheonDropdown(false);
  };

  // Request new vendor creation
  const requestNewVendor = async () => {
    if (!pantheonSearchQuery) {
      toast.error("Please enter a vendor name first");
      return;
    }

    setRequestingNewVendor(true);
    try {
      // Send email to acc@europacific.com
      await axios.post(`${API_URL}/email/send`, {
        to: "acc@europacific.com",
        subject: `[NEW VENDOR REQUEST] ${pantheonSearchQuery}`,
        body: `
New vendor creation request from Finance Panel.

Requested Vendor Name: ${pantheonSearchQuery}
Requested By: ${currentUser?.email || "Unknown"}
Source: Outlook Finance Add-in
Email Context: ${currentEmail?.subject || "N/A"}
Sender: ${currentEmail?.sender_email || "N/A"}

Please create this vendor in Pantheon and notify the requester.

---
This is an automated message from myEuropacific Finance Add-in.
        `.trim(),
        from_name: "myEuropacific Finance",
      }, getAuthHeaders());

      toast.success("Request sent to acc@europacific.com");
      setShowPantheonDropdown(false);
    } catch (error) {
      toast.error("Failed to send vendor request");
      console.error(error);
    } finally {
      setRequestingNewVendor(false);
    }
  };

  // Log solicitation
  const logSolicitation = async () => {
    // Use selected vendor from solicitation form, or fall back to matched vendor
    const vendorId = newSolicitation.vendor_id || vendorData?.vendor?.id;
    const vendorName = newSolicitation.vendor_name || vendorData?.vendor?.name;
    
    if (!vendorId) {
      toast.error("Please select a vendor");
      return;
    }

    try {
      await axios.post(`${API_URL}/vendor-ap/contact-events`, {
        vendor_id: vendorId,
        vendor_name: vendorName,
        channel: newSolicitation.channel,
        direction: newSolicitation.direction,
        topic: newSolicitation.topic,
        urgency: newSolicitation.urgency,
        notes: newSolicitation.notes,
        follow_up_date: newSolicitation.follow_up_date,
        email_subject: currentEmail?.subject,
        email_from: currentEmail?.sender_email,
      }, getAuthHeaders());

      toast.success("Contact event logged!");
      setShowLogSolicitation(false);
      setNewSolicitation({
        channel: "email",
        direction: "inbound",
        topic: "payment_request",
        urgency: "medium",
        notes: "",
        follow_up_date: "",
        vendor_id: "",
        vendor_name: "",
      });
      setSolicitationVendorQuery("");
      setSolicitationVendorResults([]);
      
      if (vendorId) {
        loadSolicitationHistory(vendorId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log contact event");
    }
  };

  // Search vendors for solicitation form
  const searchSolicitationVendors = async (query) => {
    if (!query || query.length < 2) {
      setSolicitationVendorResults([]);
      return;
    }
    
    setSolicitationVendorLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/search-companies`, {
        params: { query: query, limit: 10 },
        ...getAuthHeaders()
      });
      setSolicitationVendorResults(response.data.companies || []);
      setShowSolicitationVendorDropdown(true);
    } catch (error) {
      console.error("Failed to search vendors:", error);
      setSolicitationVendorResults([]);
    } finally {
      setSolicitationVendorLoading(false);
    }
  };

  // Select vendor for solicitation
  const selectSolicitationVendor = (company) => {
    setNewSolicitation(prev => ({
      ...prev,
      vendor_id: company.id,
      vendor_name: company.name
    }));
    setSolicitationVendorQuery(company.name);
    setShowSolicitationVendorDropdown(false);
  };

  // Approve payment order
  const approvePaymentOrder = async (orderId) => {
    try {
      await axios.post(`${API_URL}/vendor-ap/payment-orders/${orderId}/approve`, {}, getAuthHeaders());
      toast.success("Payment order approved!");
      if (vendorData?.vendor?.id) {
        loadPaymentOrders(vendorData.vendor.id);
      }
    } catch (error) {
      toast.error("Failed to approve payment order");
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat('en-EU', { 
      style: 'currency', 
      currency 
    }).format(amount || 0);
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <DollarSign className="h-8 w-8 text-amber-600" />
              <h1 className="text-xl font-bold text-gray-900">myEuropacific Finance</h1>
            </div>
            <p className="text-sm text-gray-600">Outlook Add-in</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sign In</CardTitle>
              <CardDescription>
                Use your myEuropacific credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@europacific.com"
                    required
                    data-testid="finance-addin-login-email"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="finance-addin-login-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loginLoading} data-testid="finance-addin-login-btn">
                  {loginLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-xs text-center text-gray-500 mt-4">
            Create payment orders, track vendor communications, and manage AP workflows directly from Outlook.
          </p>
        </div>
      </div>
    );
  }

  // Main add-in interface
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-amber-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            <span className="font-semibold">Finance Panel</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-amber-700"
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </div>
        
        {currentEmail && (
          <div className="mt-2 text-xs bg-amber-700 rounded px-2 py-1">
            <div className="flex items-center gap-1 truncate">
              <Mail className="h-3 w-3" />
              <span className="font-medium">{currentEmail.sender_name || currentEmail.sender_email}</span>
            </div>
            <div className="truncate opacity-80">{currentEmail.subject}</div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto border-b">
          <TabsList className="w-max min-w-full rounded-none flex">
            <TabsTrigger value="vendor" className="text-xs px-3" data-testid="tab-vendor">
              <Building2 className="h-3 w-3 mr-1" /> Vendor
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-xs px-3" data-testid="tab-payments">
              <CreditCard className="h-3 w-3 mr-1" /> Payments
            </TabsTrigger>
            <TabsTrigger value="solicitation" className="text-xs px-3" data-testid="tab-solicitation">
              <MessageSquare className="h-3 w-3 mr-1" /> Follow-up
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs px-3" data-testid="tab-invoices">
              <Receipt className="h-3 w-3 mr-1" /> Invoices
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Vendor Tab */}
        <TabsContent value="vendor" className="p-3 space-y-3">
          {vendorLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
            </div>
          ) : vendorData?.found ? (
            <div className="space-y-3">
              {/* Vendor Card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base">{vendorData.vendor.name}</h3>
                      {vendorData.vendor.email && (
                        <p className="text-sm text-gray-500">{vendorData.vendor.email}</p>
                      )}
                      {vendorData.vendor.country && (
                        <p className="text-sm text-gray-500">{vendorData.vendor.city}, {vendorData.vendor.country}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {vendorData.vendor.payment_terms && (
                          <Badge variant="outline" className="text-xs">
                            {vendorData.vendor.payment_terms}
                          </Badge>
                        )}
                        {vendorData.vendor.vendor_type && (
                          <Badge variant="secondary" className="text-xs">
                            {vendorData.vendor.vendor_type}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-2">
                <Card className="bg-amber-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-amber-700">
                      {formatCurrency(vendorData.vendor.total_outstanding || 0)}
                    </p>
                    <p className="text-xs text-gray-500">Outstanding</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-red-600">
                      {vendorData.vendor.overdue_count || 0}
                    </p>
                    <p className="text-xs text-gray-500">Overdue</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => setShowCreatePaymentOrder(true)}
                  data-testid="btn-create-payment-order"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Payment Order
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowLogSolicitation(true)}
                  data-testid="btn-log-followup"
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Log Follow-up
                </Button>
              </div>

              {/* Open Full Profile */}
              <Button 
                variant="ghost" 
                className="w-full text-sm"
                onClick={() => window.open(`${API_BASE_URL}/vendor-ap`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Full Vendor Profile
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {vendorData?.searchedEmail 
                  ? `No vendor found for domain: ${vendorData.domain}`
                  : "Open an email to lookup vendor"
                }
              </p>
              <div className="flex gap-2 justify-center mt-4">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => window.open(`${API_BASE_URL}/vendor-ap`, '_blank')}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Vendor
                </Button>
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => setShowCreatePaymentOrder(true)}
                >
                  <CreditCard className="h-3 w-3 mr-1" />
                  Manual Order
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Payment Orders Tab */}
        <TabsContent value="payments" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button 
              onClick={() => vendorData?.vendor?.id && loadPaymentOrders(vendorData.vendor.id)} 
              variant="outline" 
              size="sm" 
              disabled={paymentOrdersLoading}
            >
              {paymentOrdersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button 
              onClick={() => setShowCreatePaymentOrder(true)} 
              size="sm" 
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4 mr-1" /> New Payment Order
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-280px)]">
            {paymentOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending payment orders</p>
              </div>
            ) : (
              <div className="space-y-2">
                {paymentOrders.map((order) => (
                  <Card key={order.id} className="hover:bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{order.vendor_name}</p>
                            {order.priority === "urgent" && (
                              <Badge variant="destructive" className="text-xs">Urgent</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{order.invoice_number || "No invoice ref"}</p>
                          <p className="text-base font-semibold text-amber-700 mt-1">
                            {formatCurrency(order.amount, order.currency)}
                          </p>
                          {order.due_date && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Calendar className="h-3 w-3" />
                              Due: {new Date(order.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Badge 
                            variant={order.status === "approved" ? "default" : order.status === "pending" ? "secondary" : "outline"}
                            className="text-xs"
                          >
                            {order.status}
                          </Badge>
                          {order.status === "pending" && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 text-xs text-green-600"
                              onClick={() => approvePaymentOrder(order.id)}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Solicitation Follow-up Tab */}
        <TabsContent value="solicitation" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button 
              onClick={() => vendorData?.vendor?.id && loadSolicitationHistory(vendorData.vendor.id)} 
              variant="outline" 
              size="sm" 
              disabled={solicitationLoading}
            >
              {solicitationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button 
              onClick={() => setShowLogSolicitation(true)} 
              size="sm" 
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4 mr-1" /> Log Contact
            </Button>
          </div>

          {/* Quick Log from Current Email */}
          {currentEmail && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-600" />
                  Quick Log This Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select 
                    className="w-full border rounded px-2 py-1 text-sm"
                    id="quick-topic"
                    defaultValue="payment_request"
                  >
                    <option value="payment_request">Payment Request</option>
                    <option value="reminder">Payment Reminder</option>
                    <option value="dispute">Dispute</option>
                    <option value="inquiry">General Inquiry</option>
                  </select>
                  <select 
                    className="w-full border rounded px-2 py-1 text-sm"
                    id="quick-urgency"
                    defaultValue="medium"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <Button 
                  size="sm" 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={async () => {
                    if (!vendorData?.vendor?.id) {
                      toast.error("No vendor identified");
                      return;
                    }
                    const topic = document.getElementById('quick-topic').value;
                    const urgency = document.getElementById('quick-urgency').value;
                    
                    try {
                      await axios.post(`${API_URL}/vendor-ap/contact-events`, {
                        vendor_id: vendorData.vendor.id,
                        channel: "email",
                        direction: "inbound",
                        topic,
                        urgency,
                        notes: `Re: ${currentEmail.subject}`,
                        email_subject: currentEmail.subject,
                        email_from: currentEmail.sender_email,
                      }, getAuthHeaders());
                      
                      toast.success("Contact logged!");
                      loadSolicitationHistory(vendorData.vendor.id);
                    } catch (error) {
                      toast.error("Failed to log contact");
                    }
                  }}
                  data-testid="btn-quick-log"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Log as Inbound Contact
                </Button>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Solicitation History */}
          <ScrollArea className="h-[calc(100vh-400px)]">
            {solicitationHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No contact history</p>
              </div>
            ) : (
              <div className="space-y-2">
                {solicitationHistory.map((event) => (
                  <Card key={event.id} className="hover:bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          event.channel === "email" ? "bg-blue-100" :
                          event.channel === "call" ? "bg-green-100" :
                          event.channel === "visit" ? "bg-purple-100" : "bg-gray-100"
                        }`}>
                          {event.channel === "email" ? <Mail className="h-4 w-4 text-blue-600" /> :
                           event.channel === "call" ? <PhoneCall className="h-4 w-4 text-green-600" /> :
                           event.channel === "visit" ? <User className="h-4 w-4 text-purple-600" /> :
                           <MessageSquare className="h-4 w-4 text-gray-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm capitalize">{event.topic?.replace(/_/g, ' ')}</span>
                            <Badge 
                              variant={event.direction === "inbound" ? "outline" : "secondary"}
                              className="text-xs"
                            >
                              {event.direction}
                            </Badge>
                            {event.urgency === "high" && (
                              <Badge variant="destructive" className="text-xs">Urgent</Badge>
                            )}
                          </div>
                          {event.notes && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{event.notes}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(event.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button 
              onClick={() => vendorData?.vendor?.id && loadPendingInvoices(vendorData.vendor.id)} 
              variant="outline" 
              size="sm" 
              disabled={invoicesLoading}
            >
              {invoicesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => window.open(`${API_BASE_URL}/vendor-ap`, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" /> Open AP Module
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-260px)]">
            {pendingInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending invoices</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvoices.map((invoice) => (
                  <Card key={invoice.id} className="hover:bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{invoice.invoice_number}</p>
                          <p className="text-xs text-gray-500">{invoice.vendor_name}</p>
                          <p className="text-base font-semibold text-amber-700 mt-1">
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge 
                            variant={
                              invoice.is_overdue ? "destructive" : 
                              invoice.status === "paid" ? "default" : "secondary"
                            }
                            className="text-xs"
                          >
                            {invoice.is_overdue ? "Overdue" : invoice.status}
                          </Badge>
                          {invoice.due_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              Due: {new Date(invoice.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Create Payment Order Dialog */}
      <Dialog open={showCreatePaymentOrder} onOpenChange={(open) => {
        setShowCreatePaymentOrder(open);
        if (!open) {
          setPantheonSearchQuery("");
          setPantheonSearchResults([]);
          setShowPantheonDropdown(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Payment Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Label>Vendor Name * (Search Pantheon)</Label>
              <div className="relative">
                <Input
                  value={pantheonSearchQuery}
                  onChange={(e) => {
                    setPantheonSearchQuery(e.target.value);
                    setNewPaymentOrder(p => ({ ...p, vendor_name: e.target.value, vendor_id: "" }));
                  }}
                  onFocus={() => pantheonSearchResults.length > 0 && setShowPantheonDropdown(true)}
                  placeholder="Search Pantheon customers..."
                  data-testid="input-vendor-name"
                  className="pr-8"
                />
                {pantheonSearchLoading && (
                  <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showPantheonDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                  {pantheonSearchResults.length > 0 ? (
                    <>
                      {pantheonSearchResults.map((company) => (
                        <button
                          key={company.id}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 border-b last:border-b-0"
                          onClick={() => selectPantheonCustomer(company)}
                        >
                          <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{company.name}</div>
                            {company.city && (
                              <div className="text-xs text-gray-500 truncate">{company.city}, {company.country}</div>
                            )}
                          </div>
                        </button>
                      ))}
                      <div className="border-t bg-gray-50 p-2">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-100 rounded flex items-center justify-center gap-2"
                          onClick={requestNewVendor}
                          disabled={requestingNewVendor}
                        >
                          {requestingNewVendor ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          Request New Vendor: "{pantheonSearchQuery}"
                        </button>
                      </div>
                    </>
                  ) : pantheonSearchQuery.length >= 2 && !pantheonSearchLoading ? (
                    <div className="p-3">
                      <p className="text-sm text-gray-500 mb-2">No matching vendors found</p>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded flex items-center justify-center gap-2"
                        onClick={requestNewVendor}
                        disabled={requestingNewVendor}
                      >
                        {requestingNewVendor ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        Request New Vendor from acc@europacific.com
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              
              {/* Show selected vendor */}
              {newPaymentOrder.vendor_id && (
                <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  <span>Linked to Pantheon: {newPaymentOrder.vendor_name}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={newPaymentOrder.amount}
                  onChange={(e) => setNewPaymentOrder(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newPaymentOrder.currency}
                  onChange={(e) => setNewPaymentOrder(p => ({ ...p, currency: e.target.value }))}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Invoice Number</Label>
              <Input
                value={newPaymentOrder.invoice_number}
                onChange={(e) => setNewPaymentOrder(p => ({ ...p, invoice_number: e.target.value }))}
                placeholder="INV-2026-001"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={newPaymentOrder.invoice_date}
                  onChange={(e) => setNewPaymentOrder(p => ({ ...p, invoice_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newPaymentOrder.due_date}
                  onChange={(e) => setNewPaymentOrder(p => ({ ...p, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={newPaymentOrder.priority}
                onChange={(e) => setNewPaymentOrder(p => ({ ...p, priority: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newPaymentOrder.description}
                onChange={(e) => setNewPaymentOrder(p => ({ ...p, description: e.target.value }))}
                placeholder="Payment details..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePaymentOrder(false)}>
              Cancel
            </Button>
            <Button onClick={createPaymentOrder} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-submit-payment-order">
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Solicitation Dialog */}
      <Dialog open={showLogSolicitation} onOpenChange={(open) => {
        setShowLogSolicitation(open);
        if (!open) {
          setSolicitationVendorQuery("");
          setSolicitationVendorResults([]);
          setShowSolicitationVendorDropdown(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Vendor Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Vendor Search */}
            <div className="relative">
              <Label>Vendor * (Search Pantheon)</Label>
              <div className="relative">
                <Input
                  value={solicitationVendorQuery || vendorData?.vendor?.name || ""}
                  onChange={(e) => {
                    setSolicitationVendorQuery(e.target.value);
                    setNewSolicitation(p => ({ ...p, vendor_id: "", vendor_name: e.target.value }));
                    searchSolicitationVendors(e.target.value);
                  }}
                  onFocus={() => solicitationVendorResults.length > 0 && setShowSolicitationVendorDropdown(true)}
                  placeholder={vendorData?.vendor?.name || "Search Pantheon customers..."}
                  className="pr-8"
                />
                {solicitationVendorLoading && (
                  <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showSolicitationVendorDropdown && solicitationVendorResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {solicitationVendorResults.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 border-b last:border-b-0"
                      onClick={() => selectSolicitationVendor(company)}
                    >
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{company.name}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Show linked vendor */}
              {(newSolicitation.vendor_id || vendorData?.vendor?.id) && (
                <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  <span>Vendor: {newSolicitation.vendor_name || vendorData?.vendor?.name}</span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newSolicitation.channel}
                  onChange={(e) => setNewSolicitation(p => ({ ...p, channel: e.target.value }))}
                >
                  <option value="email">Email</option>
                  <option value="call">Phone Call</option>
                  <option value="visit">Visit</option>
                  <option value="sms">SMS/WhatsApp</option>
                </select>
              </div>
              <div>
                <Label>Direction</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newSolicitation.direction}
                  onChange={(e) => setNewSolicitation(p => ({ ...p, direction: e.target.value }))}
                >
                  <option value="inbound">Inbound (from vendor)</option>
                  <option value="outbound">Outbound (to vendor)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Topic</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newSolicitation.topic}
                  onChange={(e) => setNewSolicitation(p => ({ ...p, topic: e.target.value }))}
                >
                  <option value="payment_request">Payment Request</option>
                  <option value="reminder">Payment Reminder</option>
                  <option value="dispute">Dispute</option>
                  <option value="inquiry">General Inquiry</option>
                  <option value="negotiation">Negotiation</option>
                </select>
              </div>
              <div>
                <Label>Urgency</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newSolicitation.urgency}
                  onChange={(e) => setNewSolicitation(p => ({ ...p, urgency: e.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Follow-up Date (optional)</Label>
              <Input
                type="date"
                value={newSolicitation.follow_up_date}
                onChange={(e) => setNewSolicitation(p => ({ ...p, follow_up_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newSolicitation.notes}
                onChange={(e) => setNewSolicitation(p => ({ ...p, notes: e.target.value }))}
                placeholder="Contact details..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogSolicitation(false)}>
              Cancel
            </Button>
            <Button onClick={logSolicitation} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-submit-solicitation">
              Log Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggestion Box - Fixed at bottom */}
      <div className="sticky bottom-0 left-0 right-0 mx-3 mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg shadow-lg">
        <p className="text-[10px] font-medium text-amber-700 mb-1 flex items-center gap-1">
          <Lightbulb className="h-3 w-3" /> Have a suggestion?
        </p>
        <div className="flex gap-1">
          <Input
            value={suggestionText}
            onChange={(e) => setSuggestionText(e.target.value)}
            placeholder="Share your idea..."
            className="text-xs flex-1 bg-white h-7"
            onKeyDown={(e) => e.key === "Enter" && submitSuggestion()}
          />
          <Button 
            size="sm" 
            onClick={submitSuggestion}
            disabled={submittingSuggestion || !suggestionText.trim()}
            className="bg-amber-600 hover:bg-amber-700 h-7 px-2"
          >
            {submittingSuggestion ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
